import ApplicationController from './application_controller'

/*
 * `owner-review-queue` — Active Admin /admin/organizations/needs_review only.
 *
 * - Selection, bulk-link option intersection, export mirroring, filter date toggles
 * - Drawer (template clone into panel)
 * - POST process_needs_review: fetch + row animation + redirect (preserves filter params)
 * - Toast host portaled to document.body (fixed positioning under AA chrome)
 *
 * Server contract: Admin::OrganizationsController#needs_review_redirect_params (filter keys)
 */
export default class extends ApplicationController {
  static targets = ['checkbox', 'selectionCount', 'bulkButton', 'selectAll', 'drawer',
    'drawerContent', 'backdrop', 'creationDate', 'creationDateFrom', 'creationDateTo',
    'exportSelection', 'exportButton', 'bulkLinkTarget', 'bulkLinkButton']

  connect () {
    /*
     * Document capture: drawer apps inject cloned forms; `this.element` bubbling can miss them.
     * Capture also runs before descendant handlers that might submit without reaching the page root.
     */
    this.documentSubmitCapture = (event) => {
      if (!(event.target instanceof HTMLFormElement)) return
      if (!this.element.contains(event.target)) return

      void this.lockActions(event)
    }
    document.addEventListener('submit', this.documentSubmitCapture, true)

    this.boundCloseOnEscape = this.closeOnEscape.bind(this)
    document.addEventListener('keydown', this.boundCloseOnEscape)
    this.isSubmitting = false
    this.toastDismissTimeouts = []
    // Move toasts under <body> so position:fixed is anchored to the viewport (some AA
    // wrappers establish a containing block and break nested fixed positioning).
    this.toastHost = this.element.querySelector('.owner-review-page__toasts')
    if (this.toastHost) {
      document.body.appendChild(this.toastHost)
      this.toastHost.classList.add('owner-review-page__toasts--portaled')
    }
    this.toggleDateRangeInputs()
    this.refreshSelection()
    this.lastDrawerTrigger = null
    this.scheduleToastDismiss(this.toastHost)
  }

  disconnect () {
    if (this.documentSubmitCapture) {
      document.removeEventListener('submit', this.documentSubmitCapture, true)
      this.documentSubmitCapture = null
    }

    document.removeEventListener('keydown', this.boundCloseOnEscape)
    this.clearToastDismissTimeouts()
    if (this.toastHost?.isConnected) {
      this.toastHost.remove()
    }
    this.toastHost = null
  }

  toggleAll () {
    if (this.isSubmitting) return

    this.checkboxTargets.forEach((checkbox) => {
      checkbox.checked = this.selectAllTarget.checked
    })

    this.refreshSelection()
  }

  refreshSelection () {
    if (this.isSubmitting) return

    const checkedCount = this.checkboxTargets.filter((checkbox) => checkbox.checked).length
    const totalCount = this.checkboxTargets.length

    if (this.hasSelectionCountTarget) {
      this.selectionCountTarget.textContent = `${checkedCount} selected`
    }

    if (this.hasSelectAllTarget) {
      this.selectAllTarget.checked = checkedCount > 0 && checkedCount === totalCount
      this.selectAllTarget.indeterminate = checkedCount > 0 && checkedCount < totalCount
    }

    this.syncExportSelection()
    this.syncBulkLinkOptions()

    this.bulkButtonTargets.forEach((button) => {
      if (this.hasBulkLinkButtonTarget && button === this.bulkLinkButtonTarget) return

      button.disabled = checkedCount === 0
    })
  }

  toggleDateRangeInputs () {
    if (this.isSubmitting) return
    if (!this.hasCreationDateTarget) return

    const isCustomRange = this.creationDateTarget.value === 'custom'

    if (this.hasCreationDateFromTarget) {
      this.creationDateFromTarget.disabled = !isCustomRange
    }

    if (this.hasCreationDateToTarget) {
      this.creationDateToTarget.disabled = !isCustomRange
    }
  }

  syncExportSelection () {
    if (!this.hasExportSelectionTarget) return

    this.exportSelectionTarget.innerHTML = ''

    this.checkboxTargets
      .filter((checkbox) => checkbox.checked)
      .forEach((checkbox) => {
        const input = document.createElement('input')
        input.type = 'hidden'
        input.name = 'owner_alias_ids[]'
        input.value = checkbox.value
        this.exportSelectionTarget.appendChild(input)
      })

    if (this.hasExportButtonTarget) {
      this.exportButtonTarget.textContent =
        this.exportSelectionTarget.childElementCount > 0 ? 'Export selected' : 'Export'
    }
  }

  syncBulkLinkOptions () {
    if (!this.hasBulkLinkTargetTarget) return

    const selectedCheckboxes = this.checkboxTargets.filter((checkbox) => checkbox.checked)
    const sharedOptions = this.sharedBulkLinkOptions(selectedCheckboxes)
    const previousValue = this.bulkLinkTargetTarget.value

    this.bulkLinkTargetTarget.innerHTML = ''
    this.appendBulkLinkOption('', this.bulkLinkPlaceholder(selectedCheckboxes, sharedOptions))
    sharedOptions.forEach((option) => {
      this.appendBulkLinkOption(option.id, option.label)
    })

    if (sharedOptions.some((option) => String(option.id) === previousValue)) {
      this.bulkLinkTargetTarget.value = previousValue
    }

    if (this.hasBulkLinkButtonTarget) {
      this.bulkLinkButtonTarget.disabled =
        selectedCheckboxes.length === 0 || sharedOptions.length === 0
    }

    this.bulkLinkTargetTarget.disabled =
      selectedCheckboxes.length === 0 || sharedOptions.length === 0
  }

  sharedBulkLinkOptions (selectedCheckboxes) {
    if (selectedCheckboxes.length === 0) return []

    const optionSets = selectedCheckboxes
      .map((checkbox) => this.parseSuggestionOptions(checkbox))
      .filter((options) => options.length > 0)

    if (optionSets.length !== selectedCheckboxes.length) return []

    return optionSets.reduce((shared, options) => {
      if (!shared) return options

      return shared.filter((sharedOption) => {
        return options.some((option) => String(option.id) === String(sharedOption.id))
      })
    }, null) || []
  }

  parseSuggestionOptions (checkbox) {
    try {
      return JSON.parse(checkbox.dataset.suggestionOptions || '[]')
    } catch {
      return []
    }
  }

  bulkLinkPlaceholder (selectedCheckboxes, sharedOptions) {
    if (selectedCheckboxes.length === 0) return 'Link in bulk to...'
    if (sharedOptions.length === 0) return 'No shared suggestion for selected rows'

    return 'Link in bulk to...'
  }

  appendBulkLinkOption (value, label) {
    const option = document.createElement('option')
    option.value = value
    option.textContent = label
    this.bulkLinkTargetTarget.appendChild(option)
  }

  maybeOpenDrawer (event) {
    if (this.isSubmitting) return
    if (event.defaultPrevented) return

    const interactiveSelector = 'a, button, input, select, textarea, label, form'
    if (event.target.closest(interactiveSelector)) return

    this.openDrawer(event)
  }

  openDrawer (event) {
    if (this.isSubmitting) return
    const templateId = event.currentTarget.dataset.templateId || event.target.dataset.templateId
    const template = document.getElementById(templateId)

    if (!template) return

    this.lastDrawerTrigger = event.currentTarget
    this.lastDrawerTrigger?.setAttribute('aria-expanded', 'true')

    this.drawerContentTarget.innerHTML = template.innerHTML
    this.drawerContentTarget.scrollTop = 0
    this.drawerTarget.scrollTop = 0
    this.drawerTarget.hidden = false
    this.drawerTarget.setAttribute('aria-hidden', 'false')
    this.backdropTarget.hidden = false
    document.body.classList.add('owner-review-drawer-open')

    const closeButton = this.drawerTarget.querySelector('[data-action~="owner-review-queue#closeDrawer"]')
    closeButton?.focus()
  }

  closeDrawer () {
    this.drawerTarget.hidden = true
    this.drawerTarget.setAttribute('aria-hidden', 'true')
    this.backdropTarget.hidden = true
    this.drawerContentTarget.innerHTML = ''
    document.body.classList.remove('owner-review-drawer-open')

    this.lastDrawerTrigger?.setAttribute('aria-expanded', 'false')
    this.lastDrawerTrigger?.focus()
    this.lastDrawerTrigger = null
  }

  closeOnEscape (event) {
    if (this.isSubmitting) return
    if (event.key === 'Escape' && !this.drawerTarget.hidden) {
      this.closeDrawer()
    }
  }

  submittingFormForEvent (event) {
    if (event.target instanceof HTMLFormElement) return event.target

    if (event.submitter?.form instanceof HTMLFormElement) return event.submitter.form

    if (typeof event.target?.closest === 'function') return event.target.closest('form')

    return null
  }

  async lockActions (event) {
    const form = this.submittingFormForEvent(event)

    // Submit events can originate from implementations where target is not the form;
    // bail out instead of disabling the whole page with a non-form "activeForm".
    if (!(form instanceof HTMLFormElement)) return

    if (this.isSubmitting) {
      event.preventDefault()
      return
    }

    if (this.canAsyncReviewSubmit(form, event)) {
      event.preventDefault()
      event.stopImmediatePropagation()
      await this.performAsyncReviewSubmit(form, event.submitter)
      return
    }

    this.isSubmitting = true
    this.element.classList.add('owner-review-page--submitting')
    document.body.classList.add('owner-review-page-busy')

    this.disableInteractiveElements(form)
    this.markSubmitterBusy(event.submitter)
  }

  isProcessNeedsReviewPost (form) {
    const method = (form.getAttribute('method') || form.method || 'get').toLowerCase()
    if (method !== 'post') return false

    try {
      const u = new URL(form.action, window.location.href)
      return u.pathname.includes('process_needs_review')
    } catch (_) {
      return false
    }
  }

  buildProcessReviewFormData (form, submitter) {
    try {
      if (submitter) return new FormData(form, submitter)
    } catch (_) {}

    const fd = new FormData(form)
    if (submitter?.name === 'decision' && submitter.type === 'submit') {
      fd.set(submitter.name, submitter.value)
    }
    return fd
  }

  canAsyncReviewSubmit (form, event) {
    if (!this.isProcessNeedsReviewPost(form)) return false

    const submitter = event?.submitter
    const fd = this.buildProcessReviewFormData(form, submitter)
    const decision = fd.get('decision')
    const asyncDecisions = ['approve', 'link', 'ignore']
    if (!asyncDecisions.includes(String(decision))) return false

    const ids = fd.getAll('owner_alias_ids[]')
      .filter((id) => id != null && String(id).trim() !== '')

    if (ids.length === 0) return false

    /* Bulk toolbar only exposes `decision` on the clicked button — need submitter for FormData. */
    if (form.id === 'bulk-review-form') {
      return submitter?.name === 'decision' && asyncDecisions.includes(submitter.value)
    }

    return ids.length === 1
  }

  rowForOwnerAliasId (ownerAliasId) {
    if (ownerAliasId == null || ownerAliasId === '') return null

    const id = String(ownerAliasId)
    if (!/^\d+$/.test(id)) return null

    return this.element.querySelector(`[data-owner-alias-id="${id}"]`)
  }

  delayMs (ms) {
    return new Promise((resolve) => setTimeout(resolve, ms))
  }

  nextFrameFlush () {
    return new Promise((resolve) => {
      requestAnimationFrame(() => requestAnimationFrame(resolve))
    })
  }

  async animateValidatedRow (row) {
    if (!row) return

    await this.delayMs(0)
    row.classList.add('owner-review-page__row--validated')
    await this.nextFrameFlush()
    await this.delayMs(180)
    row.classList.add('owner-review-page__row--removing')
    await this.delayMs(300)
  }

  async animateValidatedRows (rows) {
    const list = rows.filter(Boolean)
    if (list.length === 0) return

    await Promise.all(list.map((row) => this.animateValidatedRow(row)))
  }

  csrfToken () {
    return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
  }

  /* Mirrors `needs_review_redirect_params` in admin/organizations.rb (GET query only). */
  postSuccessNeedsReviewUrlFromFormData (fd) {
    const filterKeys = [
      'search', 'creation_date', 'creation_date_from', 'creation_date_to',
      'priority', 'case_type', 'sort', 'minimum_suggestion_score',
      'low_score_threshold', 'high_score_threshold'
    ]

    const params = new URLSearchParams()

    for (const key of filterKeys) {
      const value = fd.get(key)
      if (value === '' || value == null) continue
      params.append(key, value)
    }

    const query = params.toString()
    const pathname = window.location.pathname
    const next = query ? `${pathname}?${query}` : pathname

    return new URL(next, window.location.href).href
  }

  async performAsyncReviewSubmit (form, submitter) {
    let navigated = false

    const fd = this.buildProcessReviewFormData(form, submitter)
    const ids = fd.getAll('owner_alias_ids[]')
      .filter((id) => id != null && String(id).trim() !== '')
    const rows = ids.map((id) => this.rowForOwnerAliasId(id))

    this.isSubmitting = true
    this.element.classList.add('owner-review-page--submitting')
    document.body.classList.add('owner-review-page-busy')
    this.disableInteractiveElements(form)
    this.markSubmitterBusy(submitter)

    const successUrl = this.postSuccessNeedsReviewUrlFromFormData(fd)

    const completeSuccessNavigation = async (url) => {
      if (this.hasDrawerTarget && !this.drawerTarget.hidden) this.closeDrawer()
      await this.animateValidatedRows(rows)
      navigated = true
      window.location.assign(url)
    }

    const failWithReload = () => {
      navigated = true
      this.restoreReviewSubmitChrome(submitter)
      window.location.reload()
    }

    try {
      const token = this.csrfToken()
      const headers = {
        Accept:             '*/*',
        'X-Requested-With': 'XMLHttpRequest'
      }

      if (token) headers['X-CSRF-Token'] = token

      const response = await fetch(form.action, {
        method:       'POST',
        body:         fd,
        credentials:  'same-origin',
        redirect:     'manual',
        headers
      })

      /*
       * `opaqueredirect`: cross-origin or opaque redirect — status/Location hidden.
       * Same-origin `process_needs_review` success → treat as OK and restore filters via successUrl.
       */
      if (response.type === 'opaqueredirect') {
        await completeSuccessNavigation(successUrl)
        return
      }

      if (response.status >= 300 && response.status < 400) {
        const locationHeader = response.headers.get('Location')
        const targetUrl = locationHeader
          ? new URL(locationHeader, window.location.href).href
          : successUrl

        await completeSuccessNavigation(targetUrl)
        return
      }

      /* 4xx/5xx or unexpected 200 body — no success animation; hard reload for server flash/HTML */
      failWithReload()
    } catch (_) {
      failWithReload()
    } finally {
      if (!navigated) {
        this.restoreReviewSubmitChrome(submitter)
        window.location.reload()
      }
    }
  }

  stopRowClick (event) {
    event.stopPropagation()
  }

  disableInteractiveElements (activeForm) {
    this.element.querySelectorAll('button, input, select, textarea').forEach((element) => {
      if (element.type === 'hidden') return
      if (this.belongsToActiveForm(element, activeForm)) return

      element.disabled = true
    })

    this.element.querySelectorAll('a').forEach((link) => {
      if (activeForm?.contains(link)) return

      link.setAttribute('aria-disabled', 'true')
      link.setAttribute('tabindex', '-1')
    })
  }

  belongsToActiveForm (element, activeForm) {
    if (!activeForm) return false
    if (activeForm.contains(element)) return true

    return element.form === activeForm
  }

  markSubmitterBusy (submitter) {
    if (!submitter) return

    if (submitter.tagName === 'BUTTON') {
      submitter.dataset.originalLabel = submitter.textContent
      submitter.textContent = 'Working...'
      submitter.setAttribute('aria-disabled', 'true')
    }
  }

  restoreSubmitterBusyState (submitter) {
    if (!submitter || submitter.tagName !== 'BUTTON') return

    if (submitter.dataset.originalLabel != null) {
      submitter.textContent = submitter.dataset.originalLabel
      delete submitter.dataset.originalLabel
    }

    submitter.removeAttribute('aria-disabled')
  }

  /* If reload() fails or is blocked, avoid leaving the page dimmed with "Working…". */
  restoreReviewSubmitChrome (submitter) {
    this.isSubmitting = false
    this.element.classList.remove('owner-review-page--submitting')
    document.body.classList.remove('owner-review-page-busy')
    this.restoreSubmitterBusyState(submitter)
  }

  clearToastDismissTimeouts () {
    if (!this.toastDismissTimeouts) return

    this.toastDismissTimeouts.forEach((id) => clearTimeout(id))
    this.toastDismissTimeouts = []
  }

  scheduleToastDismiss (root) {
    if (!root) return

    const toasts = root.querySelectorAll('.owner-review-page__inline-flash')
    if (toasts.length === 0) return

    toasts.forEach((toast, index) => {
      const delay = 2400 + (index * 500)

      this.toastDismissTimeouts.push(setTimeout(() => {
        toast.classList.add('owner-review-page__inline-flash--hiding')
      }, delay))

      this.toastDismissTimeouts.push(setTimeout(() => {
        toast.remove()
        if (!root.firstElementChild) root.remove()
      }, delay + 320))
    })
  }
}
